// src/components/CustomPhoneInput.js
import React, { useState } from 'react';

const countryCodes = [
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+7', country: 'RU', name: 'Russia' },
  { code: '+55', country: 'BR', name: 'Brazil' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+82', country: 'KR', name: 'South Korea' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+60', country: 'MY', name: 'Malaysia' },
  { code: '+66', country: 'TH', name: 'Thailand' },
  { code: '+62', country: 'ID', name: 'Indonesia' },
  { code: '+63', country: 'PH', name: 'Philippines' },
  { code: 'OTHER', country: 'OTHER', name: 'Other Country' }
];

// CSS Flag component using country codes
const CountryFlag = ({ countryCode, className = "" }) => {
  if (countryCode === 'OTHER') {
    return <span className={`flag-icon ${className}`}>🌍</span>;
  }
  
  return (
    <span 
      className={`flag-icon flag-icon-${countryCode.toLowerCase()} ${className}`}
      style={{
        width: '24px',
        height: '18px',
        display: 'inline-block',
        backgroundColor: '#676DFF',
        borderRadius: '2px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Fallback text if flag icons don't load */}
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '10px',
        fontWeight: 'bold',
        color: 'white',
        textAlign: 'center'
      }}>
        {countryCode}
      </span>
    </span>
  );
};

export default function CustomPhoneInput({ 
  value = '', 
  onChange, 
  placeholder = "Enter phone number",
  defaultCountry = '+91' // Default to India
}) {
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes.find(c => c.code === defaultCountry) || countryCodes[1]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [customCountryCode, setCustomCountryCode] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Initialize phone number from value prop
  React.useEffect(() => {
    if (value) {
      // Extract just the number part (remove country code if present)
      const numberPart = value.replace(selectedCountry.code, '').replace(/^\+\d+/, '');
      setPhoneNumber(numberPart);
    }
  }, [value, selectedCountry.code]);

  const handleCountrySelect = (country) => {
    console.log('Country selected:', country); // Debug log
    
    if (country.code === 'OTHER') {
      setShowCustomInput(true);
      setSelectedCountry(country);
      setIsOpen(false);
      return;
    }
    
    setShowCustomInput(false);
    setSelectedCountry(country);
    setIsOpen(false);
    // Update the full phone number with new country code
    const fullNumber = `${country.code}${phoneNumber}`;
    console.log('Full number:', fullNumber); // Debug log
    onChange && onChange(fullNumber, country.code);
  };

  const handleCustomCountryCodeChange = (e) => {
    let code = e.target.value;
    // Ensure it starts with +
    if (code && !code.startsWith('+')) {
      code = '+' + code;
    }
    setCustomCountryCode(code);
    
    // Update the selected country display
    const customCountry = {
      ...selectedCountry,
      code: code,
      name: `Custom (${code})`
    };
    setSelectedCountry(customCountry);
    
    // Update the full phone number
    const fullNumber = `${code}${phoneNumber}`;
    onChange && onChange(fullNumber, code);
  };

  const handlePhoneChange = (e) => {
    const number = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
    setPhoneNumber(number);
    const fullNumber = `${selectedCountry.code}${number}`;
    onChange && onChange(fullNumber, selectedCountry.code);
  };

  return (
    <div className="custom-phone-input">
      <div className="phone-input-container">
        {/* Country Code Dropdown */}
        <div className="country-selector">
          <button
            type="button"
            className="country-button"
            onClick={() => setIsOpen(!isOpen)}
          >
            <CountryFlag countryCode={selectedCountry.country} />
            <span className="country-code">
              {showCustomInput ? customCountryCode || selectedCountry.code : selectedCountry.code}
            </span>
            <svg 
              className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
              width="12" 
              height="8" 
              viewBox="0 0 12 8" 
              fill="none"
            >
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {/* Dropdown Menu */}
          {isOpen && (
            <div className="country-dropdown">
              <div className="dropdown-content">
                {countryCodes.map((country) => (
                  <div
                    key={country.code}
                    className="country-option"
                    onClick={() => handleCountrySelect(country)}
                  >
                    <CountryFlag countryCode={country.country} />
                    <span className="country-name">{country.name}</span>
                    <span className="country-code">
                      {country.code === 'OTHER' ? '' : country.code}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Custom Country Code Input (shown when "Other" is selected) */}
        {showCustomInput && (
          <input
            type="text"
            className="custom-country-input"
            value={customCountryCode}
            onChange={handleCustomCountryCodeChange}
            placeholder="+1"
            maxLength="6"
          />
        )}

        {/* Phone Number Input */}
        <input
          type="tel"
          className="phone-number-input"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          maxLength="15"
        />
      </div>

      <style jsx>{`
        .custom-phone-input {
          width: 100%;
          position: relative;
        }

        .phone-input-container {
          display: flex;
          background-color: #0A0A0A;
          border: 1px solid #505050;
          border-radius: 6px;
          transition: border-color 0.2s;
          overflow: visible; /* Changed from hidden to visible */
        }

        .phone-input-container:focus-within {
          border-color: #676DFF;
          box-shadow: 0 0 0 2px rgba(103, 109, 255, 0.2);
        }

        .country-selector {
          position: relative;
          flex-shrink: 0;
          z-index: 10; /* Add z-index */
        }

        .country-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: transparent;
          border: none;
          border-right: 1px solid #505050;
          color: white;
          cursor: pointer;
          transition: background-color 0.2s;
          height: 100%;
          min-width: 100px;
        }

        .country-button:hover {
          background-color: #151515;
        }

        .flag {
          font-size: 20px;
          line-height: 1;
          display: inline-block;
          width: 24px;
          text-align: center;
        }

        .flag-icon {
          display: inline-block;
          width: 24px;
          height: 18px;
          margin-right: 4px;
        }

        .country-code {
          font-size: 14px;
          color: #B8B8B8;
          min-width: 35px;
          text-align: left;
        }

        .custom-country-input {
          width: 80px;
          padding: 12px 8px;
          background: transparent;
          border: none;
          border-right: 1px solid #505050;
          color: white;
          font-size: 14px;
          outline: none;
          text-align: center;
        }

        .custom-country-input::placeholder {
          color: #808080;
        }

        .dropdown-arrow {
          color: #B8B8B8;
          transition: transform 0.2s;
          margin-left: auto;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .country-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 1000;
          background-color: #151515;
          border: 1px solid #505050;
          border-radius: 6px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
          max-height: 200px;
          overflow-y: auto;
          margin-top: 4px;
          min-width: 250px; /* Ensure dropdown is wide enough */
        }

        .dropdown-content {
          padding: 4px 0;
        }

        .country-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px; /* Increased padding for better click area */
          cursor: pointer;
          transition: background-color 0.2s;
          color: white;
          white-space: nowrap; /* Prevent text wrapping */
        }

        .country-option:hover {
          background-color: #676DFF;
        }

        .country-option .country-name {
          flex: 1;
          font-size: 14px;
        }

        .country-option .country-code {
          color: #B8B8B8;
          font-size: 12px;
          min-width: 40px;
          text-align: right;
        }

        .country-option:hover .country-code {
          color: white;
        }

        .phone-number-input {
          flex: 1;
          padding: 12px;
          background: transparent;
          border: none;
          color: white;
          font-size: 14px;
          outline: none;
        }

        .phone-number-input::placeholder {
          color: #808080;
        }

        /* Custom scrollbar for dropdown */
        .country-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .country-dropdown::-webkit-scrollbar-track {
          background: #0A0A0A;
        }

        .country-dropdown::-webkit-scrollbar-thumb {
          background: #505050;
          border-radius: 3px;
        }

        .country-dropdown::-webkit-scrollbar-thumb:hover {
          background: #676DFF;
        }
      `}</style>
    </div>
  );
}